import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Sparkles,
  Lightbulb,
  Target,
  ArrowUpRight,
  ChevronRight,
  Trophy,
  BarChart3,
  Newspaper,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ProfilePillarsCard } from './ProfilePillarsCard';

/**
 * OverviewDashboard
 * -----------------
 * A high-density, premium "Executive Presence" overview card-grid that lands
 * users on the most important signals at a glance:
 *
 *   • Authority Score donut (current → potential, with a peer-benchmark ring)
 *   • Profile Gaps (per-category status using rule-based signals)
 *   • Your Position (peer percentile + gap-to-peer pill)
 *   • Audit Activity (concrete usage we OWN: audits, posts, carousels)
 *   • AI Recommendations (top 3 gaps to close, lifted from the blueprint)
 *   • Content Ideas for You (top 3 trending topics in the user's industry)
 *
 * Reads the same shape that the Profile Optimizer already consumes
 * (``data.profile_scores`` + ``data.blueprint`` + ``data.trending_topics``)
 * so it stays in lock-step with the existing tab.
 */

const CATEGORIES = ['headline', 'about', 'experience', 'skills'];
const CATEGORY_LABELS = {
  headline: 'Headline',
  about: 'About',
  experience: 'Experience',
  skills: 'Skills',
  engagement_hooks: 'Engagement Hooks',
};

function tierForScore(v) {
  if (v >= 80) return { label: 'Strong', tone: 'success' };
  if (v >= 60) return { label: 'Good', tone: 'info' };
  if (v >= 40) return { label: 'Needs work', tone: 'warning' };
  return { label: 'Low', tone: 'destructive' };
}

/**
 * Tiering for the peer-cohort percentile.
 *
 * We deliberately reserve the celebratory "Top tier" / "Strong" framing
 * (green + trophy) for percentile >= 75. Below that we use honest, neutral
 * language so a 48th-percentile profile isn't accidentally framed as a
 * win.
 *
 * Returns:
 *   label       - the contextual descriptor to show next to the number
 *   tone        - 'success' | 'info' | 'warning' | 'destructive'
 *   showTrophy  - whether the trophy icon should render (top tiers only)
 *   ordinal     - "48th" style label for use in the headline number
 */
function tierForPercentile(p) {
  if (p == null || !Number.isFinite(p)) {
    return { label: '', tone: 'info', showTrophy: false, ordinal: '-' };
  }
  const ordinal = ordinalSuffix(p);
  if (p >= 90) return { label: 'Top tier', tone: 'success', showTrophy: true, ordinal };
  if (p >= 75) return { label: 'Strong', tone: 'success', showTrophy: true, ordinal };
  if (p >= 50) return { label: 'Mid-pack', tone: 'info', showTrophy: false, ordinal };
  if (p >= 25) return { label: 'Below median', tone: 'warning', showTrophy: false, ordinal };
  return { label: 'Room to grow', tone: 'destructive', showTrophy: false, ordinal };
}

/** Adds "st" / "nd" / "rd" / "th" suffix to a percentile rank. */
function ordinalSuffix(n) {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return `${n}`;
  const mod100 = v % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${v}th`;
  switch (v % 10) {
    case 1: return `${v}st`;
    case 2: return `${v}nd`;
    case 3: return `${v}rd`;
    default: return `${v}th`;
  }
}

/** Tailwind classes for the percentile pill keyed by tone. */
const PERCENTILE_PILL_CLASSES = {
  success: 'bg-[hsl(var(--mint))]/30 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30',
  info: 'bg-secondary text-foreground border border-border',
  warning: 'bg-[hsl(var(--warning))]/12 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30',
  destructive: 'bg-destructive/10 text-destructive border border-destructive/30',
};

function toneTextClass(tone) {
  switch (tone) {
    case 'success':
      return 'text-[hsl(var(--success))]';
    case 'info':
      return 'text-primary';
    case 'warning':
      return 'text-[hsl(var(--warning))]';
    case 'destructive':
    default:
      return 'text-destructive';
  }
}

function toneDotClass(tone) {
  switch (tone) {
    case 'success':
      return 'bg-[hsl(var(--success))]';
    case 'info':
      return 'bg-primary';
    case 'warning':
      return 'bg-[hsl(var(--warning))]';
    case 'destructive':
    default:
      return 'bg-destructive';
  }
}

/**
 * AuthorityDonut - the hero "Executive Presence" gauge.
 *
 * Renders three nested arcs so a single glance communicates:
 *   1. current overall score (primary blue, full ring background)
 *   2. peer-benchmark target (green tick mark on the same ring)
 *   3. potential ceiling (success-green arc, slightly inset)
 */
function AuthorityDonut({ current = 0, potential = 0, peer = null }) {
  const size = 220;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const innerStroke = 10;
  const rInner = r - stroke - 4;
  const cInner = 2 * Math.PI * rInner;

  const curOffset = c * (1 - Math.max(0, Math.min(100, current)) / 100);
  const potOffset = cInner * (1 - Math.max(0, Math.min(100, potential)) / 100);

  // Peer tick angle (0 deg at top, clockwise) in radians, on the OUTER ring.
  const peerPct = peer && Number.isFinite(peer.overall) ? Math.max(0, Math.min(100, peer.overall)) : null;
  const peerAngle = peerPct != null ? (peerPct / 100) * 2 * Math.PI - Math.PI / 2 : null;
  const cx = size / 2;
  const cy = size / 2;

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="overview-authority-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        {/* outer ring bg */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth={stroke} />
        {/* current overall arc (primary) */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: curOffset }}
          transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
        />
        {/* inner ring bg */}
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="hsl(var(--secondary))" strokeWidth={innerStroke} />
        {/* potential arc (success) */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={rInner}
          fill="none"
          stroke="hsl(var(--success))"
          strokeWidth={innerStroke}
          strokeLinecap="round"
          strokeDasharray={cInner}
          initial={{ strokeDashoffset: cInner }}
          animate={{ strokeDashoffset: potOffset }}
          transition={{ duration: 1.2, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 }}
          opacity={0.85}
        />
      </svg>

      {/* Peer benchmark tick on outer ring */}
      {peerAngle != null && (
        <div
          className="absolute"
          style={{
            top: cy + r * Math.sin(peerAngle) - 7,
            left: cx + r * Math.cos(peerAngle) - 7,
          }}
          data-testid="overview-peer-tick"
        >
          <div className="h-3.5 w-3.5 rounded-full bg-[hsl(var(--warning))] ring-2 ring-card shadow" />
        </div>
      )}

      {/* Center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-mono-edge text-[10px] tracking-[0.2em] text-muted-foreground">CURRENT</p>
        <p className="font-display text-5xl font-bold leading-none mt-1" data-testid="overview-current-score">
          {current}
        </p>
        <p className="font-mono-edge text-[10px] text-muted-foreground mt-1">/ 100</p>
        <p className="text-xs text-muted-foreground mt-2">
          potential <span className="text-[hsl(var(--success))] font-semibold">{potential}</span>
          {peer && Number.isFinite(peer.overall) && (
            <>
              {' '}· peer <span className="text-[hsl(var(--warning))] font-semibold">{peer.overall}</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function GapRow({ label, value, testid }) {
  const t = tierForScore(value);
  return (
    <div
      className="flex items-center justify-between py-2 border-b border-border last:border-0"
      data-testid={testid}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`h-2 w-2 rounded-full shrink-0 ${toneDotClass(t.tone)}`} />
        <span className="text-sm font-medium truncate">{label}</span>
      </div>
      <span className={`text-xs font-mono-edge tracking-tight ${toneTextClass(t.tone)}`}>{t.label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export default function OverviewDashboard({ data, me, onJumpTab }) {
  const scoring = data?.profile_scores || null;
  const bp = data?.blueprint || {};
  const topics = data?.trending_topics || [];
  const cats = scoring?.current?.categories || {};
  const potCats = scoring?.potential?.categories || {};
  const overallCurrent = scoring?.current?.overall || 0;
  const overallPotential = scoring?.potential?.overall || 0;
  const peer = scoring?.peer_benchmark || null;

  // Top 3 gap items from the blueprint (already scrubbed of visual gaps by
  // server-side strip_visual_gaps + frontend filter).
  const gaps = Array.isArray(bp.gaps) ? bp.gaps.slice(0, 3) : [];

  // Audit activity from billing snapshot (we OWN this data).
  // IMPORTANT: `me` is the *viewer's* billing snapshot, not the analysis
  // owner's. If an admin opens another user's analysis, `me` will still
  // reflect the admin's own quotas — showing that on someone else's
  // audit is misleading. Guard the card so it's only rendered when the
  // viewer is the analysis owner. `data.user_id` is the owner; `me.user_id`
  // is the viewer.
  const isOwner = !me?.user_id || !data?.user_id || me.user_id === data.user_id;
  const usage = isOwner ? (me?.usage || {}) : {};
  const auditsUsed = Number.isFinite(usage.audits_used) ? usage.audits_used : 0;
  const postsUsed = Number.isFinite(usage.posts_used) ? usage.posts_used : 0;
  const carouselsUsed = Number.isFinite(usage.carousels_used) ? usage.carousels_used : 0;

  // Audit count fallback - the engagement readiness is a nice secondary signal
  const engagement = scoring?.engagement_readiness?.current || 0;

  // Should we surface the "generate researched posts" nudge?
  // Triggers when: the analysis has completed (we have a blueprint + scoring)
  // AND the user hasn't generated any researched posts yet for this audit.
  const hasPosts = Array.isArray(data?.posts) && data.posts.length > 0;
  const analysisComplete = !!scoring && !!bp;
  const showPostsNudge = analysisComplete && !hasPosts;

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      {/* Researched Posts nudge - shown once after a fresh audit completes
          and before the user has generated any posts. Single dismissable CTA. */}
      {showPostsNudge && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="overview-posts-nudge"
        >
          <Card className="border-primary/25 bg-gradient-to-r from-primary/8 via-card to-card">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
                  <Sparkles size={16} />
                </span>
                <div className="min-w-0">
                  <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">NEXT STEP</p>
                  <p className="font-display text-lg tracking-[-0.01em] mt-0.5">
                    Turn your audit into a content calendar.
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your profile is scored. Now generate <span className="font-medium text-foreground">10 research-backed posts</span> tuned to your voice and the trending topics in your industry.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => onJumpTab?.('manus-posts')}
                className="shrink-0"
                data-testid="overview-posts-nudge-cta"
              >
                <Sparkles size={14} className="mr-2" /> Generate researched posts
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Hero band */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            EXECUTIVE PRESENCE OVERVIEW
          </p>
          <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.01em] mt-1">
            Where you stand, where you can land.
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            A framework-based view of your LinkedIn profile assessed by Blayzrr&apos;s objective-specific Profile Intelligence Framework.
            {peer && ' The peer dot is the cohort median (Enterprise*) — the green arc is what your profile unlocks after edits.'}
          </p>
        </div>
        {peer?.percentile != null && (() => {
          const tier = tierForPercentile(peer.percentile);
          return (
            <Badge
              className={`${PERCENTILE_PILL_CLASSES[tier.tone]} px-3 py-1.5`}
              data-testid="overview-percentile-badge"
            >
              {tier.showTrophy && <Trophy size={13} className="mr-1.5" />}
              {tier.ordinal} percentile*
              {tier.label ? <span className="opacity-70 ml-1.5">&middot; {tier.label}</span> : null}
            </Badge>
          );
        })()}
      </div>

      {/* Grid: hero + gaps + position */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* HERO: Authority Score */}
        <Card className="lg:col-span-1 border-border" data-testid="overview-authority-card">
          <CardContent className="p-6 flex flex-col items-center">
            <div className="w-full flex items-center justify-between mb-3">
              <div>
                <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">
                  AUTHORITY SCORE
                </p>
                <p className="font-display text-lg mt-0.5">Your Visibility</p>
              </div>
              <Sparkles size={16} className="text-primary" />
            </div>
            <AuthorityDonut current={overallCurrent} potential={overallPotential} peer={peer} />

            {/* Legend */}
            <div className="flex items-center gap-3 mt-4 text-[11px] text-muted-foreground flex-wrap justify-center">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-primary" /> Current
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--success))]" /> Potential
              </span>
              {peer && Number.isFinite(peer.overall) && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[hsl(var(--warning))]" /> Peer top-10 avg*
                </span>
              )}
            </div>

            {peer?.notes && (
              <p className="text-xs text-muted-foreground mt-3 text-center leading-relaxed" data-testid="overview-peer-notes">
                {peer.notes}
              </p>
            )}
            {peer && (
              <p className="text-[10.5px] text-muted-foreground/70 mt-2 text-center leading-snug" data-testid="overview-peer-enterprise-note">
                *Peer benchmarking is part of the Enterprise Plan.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Profile Gaps */}
        <Card className="lg:col-span-1 border-border" data-testid="overview-gaps-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">PROFILE GAPS</p>
                <p className="font-display text-lg mt-0.5">Where you&apos;re leaking points</p>
              </div>
              <BarChart3 size={16} className="text-primary" />
            </div>
            <div className="space-y-0.5">
              {CATEGORIES.map((cat) => (
                <GapRow
                  key={cat}
                  label={CATEGORY_LABELS[cat]}
                  value={cats[cat] || 0}
                  testid={`overview-gap-${cat}`}
                />
              ))}
              <GapRow
                label="Engagement Hooks"
                value={engagement}
                testid="overview-gap-engagement_hooks"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-3 text-primary hover:text-primary"
              onClick={() => onJumpTab?.('profile')}
              data-testid="overview-view-all-gaps"
            >
              View full breakdown <ChevronRight size={14} className="ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Your Position */}
        <Card className="lg:col-span-1 border-border" data-testid="overview-position-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">YOUR POSITION</p>
                <p className="font-display text-lg mt-0.5">Vs. top leaders</p>
              </div>
              <Target size={16} className="text-primary" />
            </div>

            {peer ? (
              <>
                <div className="flex items-end gap-2 mt-2">
                  <p className="font-display text-5xl font-bold leading-none" data-testid="overview-percentile-value">
                    {peer.percentile}
                  </p>
                  <p className="text-sm text-muted-foreground pb-2">/ 100 percentile*</p>
                </div>
                {(() => {
                  const tier = tierForPercentile(peer.percentile);
                  if (!tier.label) return null;
                  return (
                    <p
                      className="text-xs font-mono-edge tracking-[0.12em] uppercase mt-2"
                      style={{
                        color:
                          tier.tone === 'success'
                            ? 'hsl(var(--success))'
                            : tier.tone === 'warning'
                              ? 'hsl(var(--warning))'
                              : tier.tone === 'destructive'
                                ? 'hsl(var(--destructive))'
                                : 'hsl(var(--muted-foreground))',
                      }}
                      data-testid="overview-percentile-tier-label"
                    >
                      {tier.label}
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground mt-2">
                  You scored higher than <span className="font-semibold text-foreground">{peer.percentile}%</span> of profiles
                  in your role + industry cohort, assessed by Blayzrr&apos;s objective-specific Profile Intelligence Framework.
                </p>
                <p className="text-[10.5px] text-muted-foreground/70 mt-2 leading-snug">
                  *Peer benchmarking is part of the Enterprise Plan.
                </p>

                {/* Per-category vs peer mini bars */}
                <div className="mt-4 space-y-2" data-testid="overview-position-bars">
                  {CATEGORIES.map((cat) => {
                    const mine = cats[cat] || 0;
                    const peerV = peer.categories?.[cat] || 0;
                    const max = Math.max(100, peerV, mine);
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{CATEGORY_LABELS[cat]}</span>
                          <span>
                            <span className="text-primary font-mono-edge">{mine}</span>{' '}
                            <span className="text-muted-foreground">vs</span>{' '}
                            <span className="text-[hsl(var(--warning))] font-mono-edge">{peerV}</span>
                          </span>
                        </div>
                        <div className="relative h-1.5 bg-secondary rounded mt-1 overflow-hidden">
                          {/* peer band */}
                          <div
                            className="absolute h-full bg-[hsl(var(--warning))]/40"
                            style={{ width: `${(peerV / max) * 100}%` }}
                          />
                          {/* current bar */}
                          <div
                            className="absolute h-full bg-primary"
                            style={{ width: `${(mine / max) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="overview-position-empty">
                Peer benchmarking is part of the Enterprise Plan.*
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Discoverability / Positioning / Credibility pillars (URL-imported only) */}
      <ProfilePillarsCard pillars={data?.profile_pillars} />

      {/* Bottom row: Audit Activity + AI Recommendations + Content Ideas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Audit Activity — only for the analysis owner. Admins viewing
            someone else's audit see this slot filled by the recs card
            promoted into the first column via the parent grid layout. */}
        {isOwner && (
        <Card className="border-border" data-testid="overview-activity-card">
          <CardContent className="p-6">
            {(() => {
              // Comp / admin / any plan with no billing-period end has *lifetime*
              // usage counters — labelling them "This period" is misleading, so
              // switch the eyebrow + footer copy in that case.
              const hasPeriodEnd = !!me?.current_period_end;
              const eyebrow = hasPeriodEnd ? 'ACTIVITY THIS PERIOD' : 'LIFETIME ACTIVITY';
              const headline = hasPeriodEnd
                ? "What you've generated"
                : "What you've generated to date";
              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground" data-testid="overview-activity-eyebrow">
                        {eyebrow}
                      </p>
                      <p className="font-display text-lg mt-0.5">{headline}</p>
                    </div>
                    <TrendingUp size={16} className="text-primary" />
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <ActivityTile label="Audits" value={auditsUsed} testid="overview-activity-audits" />
                    <ActivityTile label="Posts" value={postsUsed} testid="overview-activity-posts" />
                    <ActivityTile label="Carousels" value={carouselsUsed} testid="overview-activity-carousels" />
                  </div>
                  {me?.label && (
                    <p className="text-[11px] text-muted-foreground mt-4 text-center">
                      On <span className="text-foreground font-medium">{me.label}</span>
                      {hasPeriodEnd
                        ? ` · resets ${new Date(me.current_period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                        : ''}
                    </p>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
        )}

        {/* AI Recommendations */}
        <Card className="border-border" data-testid="overview-recs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">
                  AI RECOMMENDATIONS
                </p>
                <p className="font-display text-lg mt-0.5">Top fixes to make</p>
              </div>
              <Lightbulb size={16} className="text-primary" />
            </div>
            {gaps.length > 0 ? (
              <ul className="space-y-2.5 text-sm" data-testid="overview-recs-list">
                {gaps.map((g, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-foreground/90">{typeof g === 'string' ? g : g.why || g.explanation || ''}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No gaps detected. Strong profile!</p>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-4 text-primary hover:text-primary"
              onClick={() => onJumpTab?.('profile')}
              data-testid="overview-view-all-recs"
            >
              View full blueprint <ChevronRight size={14} className="ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Content Ideas */}
        <Card className="border-border" data-testid="overview-ideas-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">
                  CONTENT IDEAS FOR YOU
                </p>
                <p className="font-display text-lg mt-0.5">Hot in your industry</p>
              </div>
              <Newspaper size={16} className="text-primary" />
            </div>
            {topics.length > 0 ? (
              <ul className="space-y-2.5" data-testid="overview-ideas-list">
                {topics.slice(0, 3).map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--sky))]/30 text-primary shrink-0 mt-0.5">
                      <ArrowUpRight size={14} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-tight line-clamp-2">{t.topic || t.title || ''}</p>
                      {t.hook_example && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.hook_example}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Trending topics still loading.</p>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-4 text-primary hover:text-primary"
              onClick={() => onJumpTab?.('inspiration')}
              data-testid="overview-view-all-ideas"
            >
              See all ideas <ChevronRight size={14} className="ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ActivityTile({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3 text-center" data-testid={testid}>
      <p className="font-display text-2xl font-bold leading-none">{value}</p>
      <p className="font-mono-edge text-[10px] tracking-[0.16em] text-muted-foreground mt-1.5 uppercase">
        {label}
      </p>
    </div>
  );
}
